import { useEffect, useState, MutableRefObject, useRef } from 'react';

const useObjectVisibility = (
  triggerTargetRef: MutableRefObject<null | Element>,
  percentVisibleToTrigger = 0.5,
  disconnectAfterFiring = true
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        // Inclusive, like the observer's own threshold: it can report a
        // ratio of exactly the threshold as the element crosses it, and
        // with a single threshold no later callback would follow.
        if (entry.intersectionRatio >= percentVisibleToTrigger) {
          // used instead of entry.isIntersecting to fix IE Edge bug
          setIsIntersecting(true);
          if (disconnectAfterFiring) {
            observer.current?.disconnect();
          }
        } else {
          setIsIntersecting(false);
        }
      });
    };
    const options = {
      root: null,
      threshold: [percentVisibleToTrigger],
    };

    if (triggerTargetRef.current) {
      observer.current = new IntersectionObserver(handleIntersect, options);
      observer.current.observe(triggerTargetRef.current);
    }

    return () => {
      observer.current?.disconnect();
    };
  }, [disconnectAfterFiring, percentVisibleToTrigger, triggerTargetRef]);

  return isIntersecting;
};

export default useObjectVisibility;
